import {
  SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES,
  SUPPORTED_AIRDROP_FARMING_EXCHANGES,
  PERSISTED_AIRDROP_FARMING_EXCHANGES,
  type PersistedAirdropFarmingExchangeId,
} from "../../constants/supported-cex";
import { SOCIAL_NETWORK } from "../../shared/campaign-types";
import {
  AIRDROP_FARMING_BOT_TYPE,
  AirdropFarmingBotTypeSchema,
  AirdropFarmingStrategySchema,
  type AirdropFarmingStrategy,
} from "./airdrop-farming.enum";
import { AIRDROP_FARMING_ERROR_REASONS, type AirdropFarmingErrorReason } from "./bot-status-labels";
import type { GridBotStopReason } from "./bot-status-labels";
import { z } from "zod";

export const AIRDROP_FARMING_RISK_PROFILE_VALUES = ["AGGRESSIVE", "BALANCED", "CONSERVATIVE"] as const;
export const AirdropFarmingRiskProfileSchema = z.enum(AIRDROP_FARMING_RISK_PROFILE_VALUES);

export const AirdropFarmingRiskProfile = {
  AGGRESSIVE: "AGGRESSIVE",
  BALANCED: "BALANCED",
  CONSERVATIVE: "CONSERVATIVE",
} as const;

export type AirdropFarmingRiskProfile = (typeof AirdropFarmingRiskProfile)[keyof typeof AirdropFarmingRiskProfile];

export const AIRDROP_FARMING_BOT_STATUS_VALUES = [
  "PENDING",
  "RUNNING",
  "PAUSED",
  "STOPPING",
  "STOPPED",
  "COMPLETED",
  "STOPPED_MAX_LOSS",
  "ERROR",
  "PAUSED_FOR_MAINTENANCE",
] as const;
export const AirdropFarmingBotStatusSchema = z.enum(AIRDROP_FARMING_BOT_STATUS_VALUES);

export const AirdropFarmingBotStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  PAUSED: "PAUSED",
  STOPPING: "STOPPING",
  STOPPED: "STOPPED",
  COMPLETED: "COMPLETED",
  STOPPED_MAX_LOSS: "STOPPED_MAX_LOSS",
  ERROR: "ERROR",
  PAUSED_FOR_MAINTENANCE: "PAUSED_FOR_MAINTENANCE",
} as const;

export type AirdropFarmingBotStatus = (typeof AirdropFarmingBotStatus)[keyof typeof AirdropFarmingBotStatus];

export { AIRDROP_FARMING_ERROR_REASONS as AIRDROP_FARMING_ERROR_REASON_VALUES } from "./bot-status-labels";
export type { AirdropFarmingErrorReason } from "./bot-status-labels";

export const AirdropFarmingErrorReasonSchema = z.enum(AIRDROP_FARMING_ERROR_REASONS);

export const AIRDROP_FARMING_MARGIN_MODE_VALUES = ["cross", "isolated"] as const;
export const AirdropFarmingMarginModeSchema = z.enum(AIRDROP_FARMING_MARGIN_MODE_VALUES);
export type AirdropFarmingMarginMode = z.infer<typeof AirdropFarmingMarginModeSchema>;

export const AirdropFarmingPerpsExchangeIdSchema = z.enum(SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES);
/** Creatable venues. Use on write paths (create-bot DTOs) so delisted venues are rejected. */
export const AirdropFarmingExchangeIdSchema = z.enum(SUPPORTED_AIRDROP_FARMING_EXCHANGES);
/** Creatable + delisted. Use on read paths that parse persisted rows. */
export const PersistedAirdropFarmingExchangeIdSchema = z.enum(PERSISTED_AIRDROP_FARMING_EXCHANGES);

// --- Venue metadata (per-exchange JSONB column) ---

export const AIRDROP_FARMING_RATE_LIMIT_TOPUP_TRIGGERS = ["start", "pre_regrid"] as const;
export const AirdropFarmingRateLimitTopupTriggerSchema = z.enum(AIRDROP_FARMING_RATE_LIMIT_TOPUP_TRIGGERS);
export type AirdropFarmingRateLimitTopupTrigger = z.infer<typeof AirdropFarmingRateLimitTopupTriggerSchema>;

export const HyperliquidRateLimitTopupSchema = z.object({
  at: z.string(),
  usdc: z.string(),
  requests: z.number(),
  trigger: AirdropFarmingRateLimitTopupTriggerSchema,
});
export type HyperliquidRateLimitTopup = z.infer<typeof HyperliquidRateLimitTopupSchema>;

export const HyperliquidRateLimitVenueMetadataSchema = z.object({
  totalTopupUsdc: z.string(),
  totalRequestsReserved: z.number(),
  topups: z.array(HyperliquidRateLimitTopupSchema),
  lastKnownBuffer: z.number(),
  lastCheckedAt: z.string(),
});
export type HyperliquidRateLimitVenueMetadata = z.infer<typeof HyperliquidRateLimitVenueMetadataSchema>;

/**
 * Venue-specific metadata stored as JSONB on `airdrop_farming_bot.venue_metadata`.
 *
 * The DB persists snake_case keys (matching the Postgres conventions used in the race-safe
 * `jsonb_set` updates in the SQL repository). TypeScript consumers see camelCase — the API
 * mapper converts between the two representations.
 */
export interface VenueMetadata {
  hyperliquid?: {
    rateLimit?: HyperliquidRateLimitVenueMetadata;
  };
}

export const VenueMetadataSchema = z.object({
  hyperliquid: z
    .object({
      rateLimit: HyperliquidRateLimitVenueMetadataSchema.optional(),
    })
    .optional(),
});

/**
 * Bot DTO schema. Includes `botType` discriminator.
 * - Perp bots: `marginMode` is "cross"|"isolated", `leverage` is 1-500, `stopLossDistanceBps` > 0
 * - Spot bots: `marginMode` is null, `leverage` is 1, `stopLossDistanceBps` is 0,
 *   `baseInventory` and `inventoryCostUsdc` are present.
 */
export const AirdropFarmingBotDtoSchema = z.object({
  id: z.number(),
  botType: AirdropFarmingBotTypeSchema,
  hubUserId: z.number(),
  exchangeAccountId: z.number().nullable(),
  // Response DTO: a bot created before its venue was delisted still renders.
  exchangeId: PersistedAirdropFarmingExchangeIdSchema,
  marginMode: AirdropFarmingMarginModeSchema.nullable(),
  pair: z.string(),
  walletAddress: z.string().nullable(),
  capitalUsdc: z.string(),
  leverage: z.number(),
  targetVolumeUsdc: z.string(),
  maxLossUsdc: z.string(),
  status: AirdropFarmingBotStatusSchema,
  lastGridAnchorPrice: z.string().nullable(),
  lastExecutionAt: z.string().nullable(),
  totalVolumeUsdc: z.string(),
  totalFeesUsdc: z.string(),
  realizedPnlUsdc: z.string(),
  unrealizedPnlUsdc: z.string(),
  totalPnlUsdc: z.string(),
  costPer1kVolume: z.string().nullable(),
  /** Cost/$1k including rate-limit top-up spend (HL only). Null when volume is zero. */
  totalCostPer1kVolume: z.string().nullable().optional(),
  fillRatePct: z.number(),
  exposurePct: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  gridSpreadBps: z.union([z.string()]),
  numLevels: z.number(),
  stopLossDistanceBps: z.number(),
  regridDriftBps: z.union([z.string()]),
  maxExposurePct: z.number(),
  unwindChunks: z.number(),
  unwindTimeoutMs: z.number(),
  minOrderSize: z.number().nullable(),
  riskProfile: AirdropFarmingRiskProfileSchema,
  strategyId: AirdropFarmingStrategySchema.optional(),
  strategyParams: z.record(z.string(), z.number()).nullable(),
  stopReason: z.string().nullable(),
  errorReason: AirdropFarmingErrorReasonSchema.nullable(),
  errorMessage: z.string().nullable(),
  // Spot-specific (null/absent for perp bots)
  baseInventory: z.string().nullable().optional(),
  inventoryCostUsdc: z.string().nullable().optional(),
  // Venue-specific metadata (JSONB passthrough + derived convenience field)
  venueMetadata: VenueMetadataSchema.nullable(),
  /**
   * Total USDC paid for Hyperliquid rate-limit top-ups.
   * Derived from `venueMetadata.hyperliquid.rateLimit.totalTopupUsdc ?? "0"`.
   */
  rateLimitTopupUsdc: z.string(),
  /**
   * ISO timestamp of the user's rating for this bot. `null` if the bot has
   * not been rated yet. Populated by the bot list endpoint; the frontend uses
   * it to show a Rate button on unrated finished bots.
   */
  ratedAt: z.string().nullable(),
  /**
   * User-controlled visibility flag. Hidden bots are filtered out of the
   * default user-facing bot list, but still appear in admin views and count
   * toward leaderboards / analytics. Only terminal bots can be hidden.
   */
  isHidden: z.boolean(),
});

export type AirdropFarmingBotDto = z.infer<typeof AirdropFarmingBotDtoSchema>;

/**
 * Server-side paginated response for `GET /v1/hub/airdrop-farming/bots`.
 * The bare-array shape was replaced with this envelope to support
 * `useInfiniteQuery` on the Flow client.
 */
export const ListAirdropFarmingBotsResponseSchema = z.object({
  bots: AirdropFarmingBotDtoSchema.array(),
  hasMore: z.boolean(),
  nextOffset: z.number().int().nullable(),
});

export type ListAirdropFarmingBotsResponse = z.infer<typeof ListAirdropFarmingBotsResponseSchema>;

/**
 * Query params accepted by `GET /v1/hub/airdrop-farming/bots`. `limit` is
 * clamped to `[1, 100]` (default 20 when omitted) and `offset` to `[0, ∞)`
 * (default 0 when omitted). Both arrive as strings on the wire — `coerce`
 * handles the conversion before the bounds checks fire.
 */
export const ListAirdropFarmingBotsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type ListAirdropFarmingBotsQuery = z.infer<typeof ListAirdropFarmingBotsQuerySchema>;

/**
 * Shared fields for creating a bot (both perp and spot).
 */
const BaseCreateBotRequestFields = {
  exchangeAccountId: z.number(),
  exchangeId: AirdropFarmingExchangeIdSchema,
  pair: z.string(),
  capitalUsdc: z.string(),
  targetVolumeUsdc: z.string().optional(),
  maxLossUsdc: z.string(),
  riskProfile: AirdropFarmingRiskProfileSchema.optional().default("BALANCED"),
  strategyId: AirdropFarmingStrategySchema.optional(),
  // Grid config overrides (all optional)
  gridSpreadBps: z.number().int().min(1).optional(),
  numLevels: z.number().int().min(1).max(10).optional(),
  regridDriftBps: z.number().int().min(1).optional(),
  maxExposurePct: z.number().int().min(1).max(100).optional(),
  unwindChunks: z.number().int().min(1).max(10).optional(),
  unwindTimeoutMs: z.number().int().min(5000).optional(),
};

export const CreatePerpBotRequestSchema = z.object({
  ...BaseCreateBotRequestFields,
  botType: z.literal(AIRDROP_FARMING_BOT_TYPE.PERP),
  marginMode: AirdropFarmingMarginModeSchema,
  walletAddress: z.string().optional(),
  leverage: z.number().min(1).max(500),
  stopLossDistanceBps: z.number().int().min(1).optional(),
});

export const CreateSpotBotRequestSchema = z.object({
  ...BaseCreateBotRequestFields,
  botType: z.literal(AIRDROP_FARMING_BOT_TYPE.SPOT),
});

export const CreateAirdropFarmingBotRequestSchema = z.discriminatedUnion("botType", [
  CreatePerpBotRequestSchema,
  CreateSpotBotRequestSchema,
]);

export type CreateAirdropFarmingBotRequest = z.infer<typeof CreateAirdropFarmingBotRequestSchema>;

export const AirdropFarmingMinCapitalResponseSchema = z.object({
  minCapitalUsdc: z.string(),
  leverage: z.number(),
  numLevels: z.number(),
  maxLossUsdc: z.string(),
});

export type AirdropFarmingMinCapitalResponse = z.infer<typeof AirdropFarmingMinCapitalResponseSchema>;

// --- Available Capital ---
// Used by the launch-bot Configure step to cap the capital input at
// `totalEquity - committed capital of other active bots on the same account`,
// where committed = capital + realizedPnL - fees. USDC string fixed to 2
// decimal places.
export const AirdropFarmingAvailableCapitalResponseSchema = z.object({
  availableForNewBotUsdc: z.string(),
});

export type AirdropFarmingAvailableCapitalResponse = z.infer<typeof AirdropFarmingAvailableCapitalResponseSchema>;

// --- Fee Tier ---

export const AirdropFarmingFeeTierResponseSchema = z
  .object({
    tier: z.number(),
    makerFee: z.number(),
    takerFee: z.number(),
  })
  .nullable();
export type AirdropFarmingFeeTierResponse = z.infer<typeof AirdropFarmingFeeTierResponseSchema>;

// --- Social Account Projection (all linked socials for a user) ---

export const SocialAccountProjectionSchema = z.object({
  socialName: z.nativeEnum(SOCIAL_NETWORK),
  username: z.string(),
  avatarUrl: z.string().nullable(),
});
export type SocialAccountProjection = z.infer<typeof SocialAccountProjectionSchema>;

// --- Volume Leaderboard ---

export const AirdropFarmingLeaderboardEntrySchema = z.object({
  rank: z.number(),
  /** Truncated `0xAAAA...BBBB` form. Full addresses are never exposed. */
  displayAddress: z.string(),
  totalVolumeUsdc: z.string(),
  /** Username of the user's preferred social account (without `@`), when linked. */
  socialUsername: z.string().optional(),
  /** Absolute CDN URL for the user's social avatar, when available. */
  socialAvatarUrl: z.string().optional(),
  /** Which social network the username/avatar belong to. */
  socialNetwork: z.nativeEnum(SOCIAL_NETWORK).optional(),
  /** All connected social accounts for hover card display. */
  allSocials: z.array(SocialAccountProjectionSchema).optional(),
  /**
   * True when the row's hub user is an ambassador (referral program). Used
   * by the Cede Flow leaderboard to render the purple/sparkles treatment
   * and the "Ambassador" chip in the hover card. Optional + defaults to
   * `false` so older cached rows don't break the client.
   */
  isAmbassador: z.boolean().optional().default(false),
});
export type AirdropFarmingLeaderboardEntry = z.infer<typeof AirdropFarmingLeaderboardEntrySchema>;

export const AirdropFarmingLeaderboardResponseSchema = z.object({
  entries: z.array(AirdropFarmingLeaderboardEntrySchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
  }),
  exchanges: z.array(z.string()),
});
export type AirdropFarmingLeaderboardResponse = z.infer<typeof AirdropFarmingLeaderboardResponseSchema>;

export const AirdropFarmingLeaderboardStatsResponseSchema = z.object({
  totalVolumeUsdc: z.string(),
  totalTraders: z.number(),
});
export type AirdropFarmingLeaderboardStatsResponse = z.infer<typeof AirdropFarmingLeaderboardStatsResponseSchema>;

export const AirdropFarmingLeaderboardPositionResponseSchema = z
  .object({
    rank: z.number(),
    totalVolumeUsdc: z.string(),
  })
  .nullable();
export type AirdropFarmingLeaderboardPositionResponse = z.infer<typeof AirdropFarmingLeaderboardPositionResponseSchema>;

// --- Cede Flow aggregate stats bar ---

/**
 * Aggregated totals for the current user's bots (both market-making and
 * delta-neutral), summed across **all** of their rows including hidden bots.
 *
 * The Cede Flow `GET /v1/hub/airdrop-farming/bots` listing is now paginated
 * and filters hidden bots out — aggregating the visible page client-side
 * therefore under-counts. This endpoint is the source of truth for the
 * top-of-page stats bar. Backed by a 30s cache to keep DB load low.
 *
 * All numeric values are decimal strings to preserve precision over JSON.
 */
export const AirdropFarmingBotAggregatesResponseSchema = z.object({
  totalVolumeUsdc: z.string(),
  totalFeesUsdc: z.string(),
  /** MM realized + unrealized + DN net PnL, summed. */
  totalPnlUsdc: z.string(),
  /** DN open interest only — MM bots don't carry net OI. */
  totalOpenInterestUsdc: z.string(),
  /** Combined count of MM + DN bots owned by the user. */
  totalBots: z.number().int(),
});

export type AirdropFarmingBotAggregatesResponse = z.infer<typeof AirdropFarmingBotAggregatesResponseSchema>;

// --- Cede Flow Performance tab ---

/**
 * Per-exchange aggregate for the Performance tab. One row per on-the-wire
 * exchange ID (e.g. `hyperliquid`, `tradexyz`, `paradex`, `mexc`); the Flow
 * UI collapses HIP-3 sub-DEXes into the `hyperliquid` tab and exposes the
 * breakdown via its own constants — the backend stays UI-agnostic.
 *
 * DN bots with cross-exchange legs emit one row per leg with OI / funding /
 * PnL split 50/50, matching what the previous client-side reducer surfaced.
 *
 * All decimal values are strings to preserve precision across the wire.
 */
export const AirdropFarmingBotPerformancePerExchangeSchema = z.object({
  exchangeId: z.string(),
  totalVolumeUsdc: z.string(),
  totalFeesUsdc: z.string(),
  totalPnlUsdc: z.string(),
  totalCapitalUsdc: z.string(),
  totalOpenInterestUsdc: z.string(),
  totalFundingCostUsdc: z.string(),
  activeBots: z.number().int(),
  totalBots: z.number().int(),
});
export type AirdropFarmingBotPerformancePerExchange = z.infer<typeof AirdropFarmingBotPerformancePerExchangeSchema>;

export const AirdropFarmingBotPerformanceResponseSchema = z.object({
  perExchange: z.array(AirdropFarmingBotPerformancePerExchangeSchema),
});

export type AirdropFarmingBotPerformanceResponse = z.infer<typeof AirdropFarmingBotPerformanceResponseSchema>;

// --- Bot Rating ---

export const AirdropFarmingPendingRatingResponseSchema = z.object({
  pendingBotId: z.number().nullable(),
  pendingBotType: z.enum(["grid", "delta-neutral"]).nullable(),
});
export type AirdropFarmingPendingRatingResponse = z.infer<typeof AirdropFarmingPendingRatingResponseSchema>;
// --- Per-competition Order-Fill Leaderboard ---

export const OrderFillLeaderboardEntrySchema = z.object({
  hubUserId: z.number(),
  exchangeId: z.string(),
  /** Truncated `0xAAAA...BBBB` form. Full addresses are never exposed. */
  displayAddress: z.string(),
  /** Decimal serialized as a string to preserve precision across the wire. */
  volumeUsd: z.string(),
  rank: z.number(),
  socialUsername: z.string().optional(),
  socialAvatarUrl: z.string().optional(),
  socialNetwork: z.nativeEnum(SOCIAL_NETWORK).optional(),
  /** All connected social accounts for hover card display. */
  allSocials: z.array(SocialAccountProjectionSchema).optional(),
});
export type OrderFillLeaderboardEntry = z.infer<typeof OrderFillLeaderboardEntrySchema>;

export const OrderFillLeaderboardResponseSchema = z.object({
  /** Top-N entries (capped by the `limit` query param). */
  entries: z.array(OrderFillLeaderboardEntrySchema),
  /**
   * Total number of distinct traders in the competition (rows in the
   * leaderboard aggregate with non-zero volume), independent of `limit`.
   * Use this for pagination / "X traders" copy on the client.
   */
  totalTraders: z.number(),
});
export type OrderFillLeaderboardResponse = z.infer<typeof OrderFillLeaderboardResponseSchema>;

export const OrderFillLeaderboardPositionResponseSchema = OrderFillLeaderboardEntrySchema.nullable();
export type OrderFillLeaderboardPositionResponse = z.infer<typeof OrderFillLeaderboardPositionResponseSchema>;

export const OrderFillLeaderboardStatsResponseSchema = z.object({
  /** Decimal serialized as a string to preserve precision across the wire. */
  totalVolumeUsd: z.string(),
  totalTraders: z.number(),
});
export type OrderFillLeaderboardStatsResponse = z.infer<typeof OrderFillLeaderboardStatsResponseSchema>;

// Traded persisted leaderboard volume per slug; client sums members for series volume.
// Unknown slugs omitted; values are strings to preserve decimal precision.
export const OrderFillVolumesBySlugsResponseSchema = z.object({
  volumeBySlug: z.record(z.string(), z.string()),
});
export type OrderFillVolumesBySlugsResponse = z.infer<typeof OrderFillVolumesBySlugsResponseSchema>;

// --- Pair Suggestion ---

export const AirdropFarmingPairDataSchema = z.object({
  symbol: z.string(),
  baseAsset: z.string(),
  markPrice: z.string(),
  priceChange24h: z.number(),
  volume24hRaw: z.string(),
  openInterest: z.string().optional(),
  maxLeverage: z.coerce.number(),
  isGrowthMode: z.boolean(),
  /** Hibachi sub-account category: "crypto" (default perps) or "fx" (currency pairs). */
  category: z.enum(["crypto", "fx"]).optional(),
  /** False when paused (e.g. Hibachi FX outside forex hours). Treated as active when omitted. */
  active: z.boolean().optional(),
});

export type AirdropFarmingPairData = z.infer<typeof AirdropFarmingPairDataSchema>;

export const AirdropFarmingSuggestPairsResponseSchema = z.object({
  exchangeId: z.string(),
  pairs: z.array(AirdropFarmingPairDataSchema),
  fetchedAt: z.string(),
});

export type AirdropFarmingSuggestPairsResponse = z.infer<typeof AirdropFarmingSuggestPairsResponseSchema>;

// --- Scheduled Maintenance Windows ---

export const MAINTENANCE_WINDOW_STATUS_VALUES = ["scheduled", "active", "past", "cancelled"] as const;
export const MaintenanceWindowStatusSchema = z.enum(MAINTENANCE_WINDOW_STATUS_VALUES);
export type MaintenanceWindowStatus = z.infer<typeof MaintenanceWindowStatusSchema>;

/**
 * Bot-creation freeze buffer for an exchange-maintenance window. Bot creation
 * is blocked on `[startAt - BOT_CREATION_BUFFER_MINUTES, endAt)` for any
 * non-cancelled, non-resumed window. Shared between the backend's
 * `Bot.blocksBotCreation` predicate and Flow's launch-CTA gate.
 *
 * The buffer is wider than the default `leadTimeMinutes` (10) so by the time
 * the scheduler dispatches the pause, the create-bot endpoint is already
 * refusing new bots — no race where a freshly created bot slips in past
 * `pauseDispatchedAt` and runs unprotected into the outage.
 */
export const BOT_CREATION_BUFFER_MINUTES = 15;

/**
 * Public DTO for an exchange maintenance window. Returned by the user-facing
 * read endpoint at `GET /v1/hub/airdrop-farming/bots/maintenance-windows`.
 * Mirrors `toPublicMaintenanceWindowDto` in the API — `adminNote` and
 * `createdBy` are intentionally absent because they are admin-only metadata
 * and must not be exposed to Flow over the wire.
 */
export const MaintenanceWindowDtoSchema = z.object({
  id: z.string(),
  exchangeId: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  leadTimeMinutes: z.number(),
  userMessage: z.string(),
  pauseDispatchedAt: z.string().nullable(),
  resumeDispatchedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  status: MaintenanceWindowStatusSchema,
});
export type MaintenanceWindowDto = z.infer<typeof MaintenanceWindowDtoSchema>;

/**
 * Response shape for `GET /v1/hub/airdrop-farming/bots/maintenance-windows`.
 * `window` is the blocking window for the queried exchange, or null when none.
 */
export const GetActiveMaintenanceWindowResponseSchema = z.object({
  window: MaintenanceWindowDtoSchema.nullable(),
});
export type GetActiveMaintenanceWindowResponse = z.infer<typeof GetActiveMaintenanceWindowResponseSchema>;

// --- Supported Exchanges ---

/**
 * HIP-3 DEX name mapping — used by the SDK to prefix coins in Hyperliquid's
 * candleSnapshot / metaAndAssetCtxs API calls.
 *
 * - "tradexyz" uses DEX name "xyz" (coin = "xyz:TSLA")
 * - Others use exchange ID as DEX name (coin = "flx:TSLA")
 */
export const HIP3_DEX_NAMES = {
  tradexyz: { dex: "xyz" },
  flx: { dex: "flx" },
  vntl: { dex: "vntl" },
  hyna: { dex: "hyna" },
  km: { dex: "km" },
  cash: { dex: "cash" },
  para: { dex: "para" },
  mkts: { dex: "mkts" },
} as const;

// --- Admin dashboard types (shared between API and Cody) ---

export { GRID_BOT_STOP_REASONS, DELTA_NEUTRAL_STOP_REASONS } from "./bot-status-labels";
export type { GridBotStopReason, DeltaNeutralStopReason } from "./bot-status-labels";

export type AirdropFarmingAdminBotDto = {
  id: number;
  status: AirdropFarmingBotStatus;
  stopReason: GridBotStopReason | null;
  errorReason: AirdropFarmingErrorReason | null;
  errorMessage: string | null;
  hubUserId: number;
  walletAddress: string | null;
  exchangeId: PersistedAirdropFarmingExchangeId;
  pair: string;
  capitalUsdc: string;
  leverage: number;
  targetVolumeUsdc: string;
  totalVolumeUsdc: string;
  fillRatePct: number;
  realizedPnlUsdc: string;
  unrealizedPnlUsdc: string;
  totalPnlUsdc: string;
  totalFeesUsdc: string;
  builderFeesUsdc: string;
  costPer1kVolume: string | null;
  /** Cost/$1k including rate-limit top-up spend (HL only). Null when volume is zero. */
  totalCostPer1kVolume: string | null;
  marginMode: AirdropFarmingMarginMode | null;
  riskProfile: AirdropFarmingRiskProfile;
  strategyId?: AirdropFarmingStrategy;
  gridSpreadBps: string;
  maxLossUsdc: string;
  maxExposurePct: number;
  numLevels: number;
  durationSeconds: number;
  lastExecutionAt: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  venueMetadata: VenueMetadata | null;
  /**
   * Total USDC paid for Hyperliquid rate-limit top-ups.
   * Derived from `venueMetadata.hyperliquid.rateLimit.totalTopupUsdc ?? "0"`.
   */
  rateLimitTopupUsdc: string;
};

export type AirdropFarmingBotStatusSummary = {
  totalRunning: number;
  totalPaused: number;
  totalCompleted: number;
  totalError: number;
  totalStopped: number;
  totalStoppedMaxLoss: number;
};

export type AirdropFarmingGlobalMetrics = {
  totalVolumeUsdc: string;
  totalPnlUsdc: string;
  averagePnlUsdc: string;
  medianPnlUsdc: string;
  averageCostPer1kUsdc: string;
  averageVolumeUsdc: string;
  medianVolumeUsdc: string;
  totalBuilderFeesUsdc: string;
  totalBots: number;
  uniqueUsers: number;
};

export type AirdropFarmingAdminListBotsResponse = {
  globalMetrics: AirdropFarmingGlobalMetrics;
  summary: AirdropFarmingBotStatusSummary;
  bots: AirdropFarmingAdminBotDto[];
  users: Array<{
    hubUserId: number;
    walletAddress: string | null;
    socials: Array<{ socialName: string; username: string | null; userId: string | null }>;
  }>;
  exchanges: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
};

export type AirdropFarmingCostPer1kBucket = {
  date: string;
  exchangeId: string;
  costPer1kUsdc: number;
  totalVolumeUsdc: number;
  totalFeesUsdc: number;
  realizedPnlUsdc: number;
  botCount: number;
};

export type AirdropFarmingCostPer1kResponse = {
  buckets: AirdropFarmingCostPer1kBucket[];
  exchanges: string[];
};

export type AirdropFarmingDailyStatsBotsByStatus = {
  running: number;
  completed: number;
  stopped: number;
  stoppedMaxLoss: number;
  error: number;
  paused: number;
  pausedForMaintenance: number;
};

export type AirdropFarmingDailyStatsBucket = {
  date: string;
  totalVolumeUsdc: number;
  expectedVolumeUsdc: number;
  uniqueUsers: number;
  medianVolumePerUser: number;
  totalBuilderFeesUsdc: number;
  botsLaunched: number;
  newUsers: number;
  botsByStatus: AirdropFarmingDailyStatsBotsByStatus;
  volumeByExchange: Record<string, number>;
  builderFeesByExchange: Record<string, number>;
};

export type AirdropFarmingDailyStatsResponse = {
  buckets: AirdropFarmingDailyStatsBucket[];
  exchanges: string[];
};

// --- Delta-Neutral Strategy ---

export const DELTA_NEUTRAL_BOT_STATUS_VALUES = [
  "PENDING",
  "OPENING",
  "RUNNING",
  "CLOSING",
  "STOPPED",
  "STOPPED_MAX_LOSS",
  "ERROR",
] as const;
export const DeltaNeutralBotStatusSchema = z.enum(DELTA_NEUTRAL_BOT_STATUS_VALUES);

export const DeltaNeutralBotStatus = {
  PENDING: "PENDING",
  OPENING: "OPENING",
  RUNNING: "RUNNING",
  CLOSING: "CLOSING",
  STOPPED: "STOPPED",
  STOPPED_MAX_LOSS: "STOPPED_MAX_LOSS",
  ERROR: "ERROR",
} as const;

export type DeltaNeutralBotStatus = (typeof DeltaNeutralBotStatus)[keyof typeof DeltaNeutralBotStatus];

export const DeltaNeutralBotDtoSchema = z.object({
  id: z.number(),
  hubUserId: z.number(),
  status: DeltaNeutralBotStatusSchema,
  legAExchangeId: z.string(),
  legAExchangeAccountId: z.number().nullable(),
  legAPair: z.string(),
  legACapitalUsdc: z.string(),
  legALeverage: z.number(),
  legAEntryPrice: z.string().nullable(),
  legAUnrealizedPnlUsdc: z.string(),
  legAFundingRate: z.string().nullable(),
  legBExchangeId: z.string(),
  legBExchangeAccountId: z.number().nullable(),
  legBPair: z.string(),
  legBCapitalUsdc: z.string(),
  legBLeverage: z.number(),
  legBEntryPrice: z.string().nullable(),
  legBUnrealizedPnlUsdc: z.string(),
  legBFundingRate: z.string().nullable(),
  maxLossUsdc: z.string(),
  totalOpenInterestUsdc: z.string(),
  totalFundingCostUsdc: z.string(),
  realizedPnlUsdc: z.string(),
  netPnlUsdc: z.string(),
  stopReason: z.string().nullable(),
  errorReason: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  openedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  // Computed fields from list query
  legANotionalUsdc: z.string().optional(),
  legBNotionalUsdc: z.string().optional(),
  isDifferentAssets: z.boolean().optional(),
  estimatedDailyFundingCostUsdc: z.string().nullable().optional(),
  /**
   * Date of the user's rating for this bot. `null` if not rated. Populated by
   * the DN bot list endpoint; the frontend uses it to show a Rate button on
   * unrated finished bots.
   */
  ratedAt: z.coerce.date().nullable().optional(),
  /**
   * True if the user has hidden this bot from their Cede Flow list. Hidden
   * bots remain visible to leaderboards / analytics — only the user-facing
   * list query filters them out by default.
   */
  isHidden: z.boolean(),
});

export type DeltaNeutralBotDto = z.infer<typeof DeltaNeutralBotDtoSchema>;

export type DeltaNeutralLegConfig = {
  exchangeId: string;
  exchangeAccountId: number;
  pair: string;
  walletAddress?: string;
  marginMode: "cross" | "isolated";
  capitalUsdc: string;
  leverage: number;
};

export type CreateDeltaNeutralBotRequest = {
  legA: DeltaNeutralLegConfig;
  legB: DeltaNeutralLegConfig;
  maxLossUsdc: string;
};

export type DeltaNeutralLegInfoRequest = {
  exchangeId: string;
  pair: string;
  exchangeAccountId: number;
  marginMode: "cross" | "isolated";
  walletAddress?: string | null;
};

export const DeltaNeutralLegInfoResponseSchema = z.object({
  freeBalanceUsdc: z.string(),
  fundingRate: z.string(),
  minNotionalUsdc: z.string().nullable(),
});

export type DeltaNeutralLegInfoResponse = z.infer<typeof DeltaNeutralLegInfoResponseSchema>;

export const AirdropFarmingBuilderFeesResponseSchema = z.record(z.string(), z.string());
export type AirdropFarmingBuilderFeesResponse = z.infer<typeof AirdropFarmingBuilderFeesResponseSchema>;

export * from "./airdrop-farming.enum";
export * from "./bot-status-labels";
